import React from 'react';
import { FileText, Search, AlertTriangle, ArrowRight, BarChart3 } from 'lucide-react';

export interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: 'upload' | 'compare' | 'analyze' | 'report';
  onClick?: () => void;
  disabled?: boolean;
}

export interface QuickActionsSectionProps {
  onUpload?: () => void;
  onCompare?: () => void;
  onAnalyze?: () => void;
  onGenerateReport?: () => void;
  loading?: boolean;
}

// Skeleton loader for individual action items
const ActionItemSkeleton: React.FC = () => (
  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg animate-pulse">
    <div className="flex items-center">
      <div className="h-10 w-10 rounded-lg bg-neutral-200 mr-3" />
      <div className="space-y-2">
        <div className="h-4 w-28 bg-neutral-200 rounded" />
        <div className="h-3 w-48 bg-neutral-200 rounded" />
      </div>
    </div>
    <div className="h-5 w-5 bg-neutral-200 rounded" />
  </div>
);

// Full skeleton loader component
const QuickActionsSectionSkeleton: React.FC = () => (
  <div
    className="bg-white rounded-xl shadow-card border border-neutral-100 p-6"
    data-testid="quick-actions-skeleton"
  >
    <div className="h-6 w-28 bg-neutral-200 rounded mb-4 animate-pulse" />
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <ActionItemSkeleton key={i} />
      ))}
    </div>
  </div>
);

const QuickActionsSection: React.FC<QuickActionsSectionProps> = ({
  onUpload,
  onCompare,
  onAnalyze,
  onGenerateReport,
  loading = false,
}) => {
  if (loading) {
    return <QuickActionsSectionSkeleton />;
  }

  const getActionIcon = (icon: QuickAction['icon']) => {
    switch (icon) {
      case 'upload':
        return <FileText className="h-5 w-5 text-primary-600" />;
      case 'compare':
        return <Search className="h-5 w-5 text-secondary-600" />;
      case 'analyze':
        return <AlertTriangle className="h-5 w-5 text-warning-600" />;
      case 'report':
        return <BarChart3 className="h-5 w-5 text-success-600" />;
    }
  };

  const getIconBackground = (icon: QuickAction['icon']) => {
    switch (icon) {
      case 'upload':
        return 'bg-primary-100 group-hover:bg-primary-200';
      case 'compare':
        return 'bg-secondary-100 group-hover:bg-secondary-200';
      case 'analyze':
        return 'bg-warning-100 group-hover:bg-warning-200';
      case 'report':
        return 'bg-success-100 group-hover:bg-success-200';
    }
  };

  const actions: QuickAction[] = [
    {
      id: 'upload',
      label: 'Upload Contract',
      description: 'Add a new Medicaid contract for analysis',
      icon: 'upload',
      onClick: onUpload,
    },
    {
      id: 'compare',
      label: 'Compare Contracts',
      description: 'Compare two contracts',
      icon: 'compare',
      onClick: onCompare,
    },
    {
      id: 'analyze',
      label: 'Risk Analysis',
      description: 'Identify compliance risks and CMS violations',
      icon: 'analyze',
      onClick: onAnalyze,
    },
    {
      id: 'report',
      label: 'Generate Report',
      description: 'Create executive summary or detailed report',
      icon: 'report',
      onClick: onGenerateReport,
    },
  ];

  return (
    <div
      className="bg-white rounded-xl shadow-card border border-neutral-100 p-6
                 transition-all duration-200 ease-in-out hover:shadow-card-hover"
      data-testid="quick-actions-section"
    >
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">Quick Actions</h3>
      <div className="space-y-2">
        {actions.map((action) => (
          <button
            key={action.id}
            className="w-full flex items-center justify-between p-3 bg-neutral-50 rounded-lg
                       hover:bg-neutral-100 transition-all duration-150 group
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-neutral-50
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={action.onClick}
            disabled={action.disabled}
            data-testid={`action-${action.id}`}
          >
            <div className="flex items-center min-w-0">
              <div
                className={`h-10 w-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0
                           transition-all duration-200 ${getIconBackground(action.icon)}
                           group-hover:scale-105`}
              >
                {getActionIcon(action.icon)}
              </div>
              <div className="text-left min-w-0">
                <p className="font-medium text-neutral-900 truncate">{action.label}</p>
                <p className="text-sm text-neutral-500 truncate">{action.description}</p>
              </div>
            </div>
            <ArrowRight
              className="h-5 w-5 text-neutral-400 group-hover:text-primary-600
                         transition-all duration-200 group-hover:translate-x-1 flex-shrink-0 ml-2"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuickActionsSection;
